import * as React from "react";
import { useState } from "react";
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Menu, MenuItem, Divider, Checkbox,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  useSaveFieldAdminTeamInstallerMutation,
  useSetFieldAdminTeamInstallerStatusMutation,
  useSaveFieldAdminVehicleMutation,
} from "./fieldAdminApi";
import { BORDER, AMBER } from "../../Global/colors";
import { showSuccess, showError } from "../../Global/notifications/notificationsSlice";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";
import { FAFieldError } from "./FAStates";
import { FATextField } from "./StockFormShell";
import { FA_DIALOG_PAPER_SX, FA_MENU_PAPER_SX } from "./FALayout";
import { teamLabel } from "./designTokens";

// The Team page's dialogs and row menu. Split out because Team.js was 723 lines, of
// which these were 269 — each is a standalone form with its own state and mutations,
// and none of them is referenced anywhere else on the page beyond being rendered.

export function EditTeamDialog({ overview, onClose }) {
  // Read-only by design. The design's own "Edit team" button does exactly this —
  // its JS opens a confirm that saves nothing — and there is no Team entity to
  // persist a name to anyway. Area and install target are shown as the real values
  // they are, but stay PM-owned.
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box sx={{ flex: 1, fontSize: "1.05rem", fontWeight: 700 }}>Edit team</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.45)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: BORDER }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", mb: "16px" }}>
          <ReadOnlyField label="Company" value={overview.company_name || "—"} />
          <ReadOnlyField label="Area" value={overview.area_name || "—"} />
          <ReadOnlyField label="Install target" value={overview.install_target != null ? String(overview.install_target) : "Not set"} />
        </Box>
        <Typography sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", p: "10px 12px", fontSize: "0.74rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
          Area, install target and timeframe are set by the Project Manager and stay read-only here. Team creation and retirement remain IMS-only. Roster membership and vehicle assignment are edited from the sections on this page.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton onClick={onClose}>Close</FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export function ReadOnlyField({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "5px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.85)" }}>{value}</Typography>
    </Box>
  );
}

export function RowActionsMenu({ rowMenu, onClose, onEdit, onRemove }) {
  const installer = rowMenu?.installer;
  return (
    <Menu
      anchorEl={rowMenu?.anchorEl}
      open={Boolean(rowMenu)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: FA_MENU_PAPER_SX }}
    >
      <MenuItem onClick={() => { onClose(); if (installer) onEdit(installer); }}>
        <EditOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Edit installer
      </MenuItem>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <MenuItem onClick={() => { onClose(); if (installer) onRemove(installer); }} data-danger="true">
        <ArchiveOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Remove installer
      </MenuItem>
    </Menu>
  );
}

export function InstallerDialog({ installer, currentQE, onClose }) {
  const dispatch = useDispatch();
  const isEdit = Boolean(installer.id);
  const [saveInstaller, { isLoading: isSaving, error }] = useSaveFieldAdminTeamInstallerMutation();

  // Anchored on isEdit, not just the id comparison. In add mode `installer` is the {}
  // sentinel, so installer.id is undefined — and on a team with no QE assigned,
  // currentQE?.ims_installer_id is undefined too. The bare comparison was therefore
  // `undefined === undefined` → true, which both pre-ticked the QE box on "Add installer"
  // and set wasQE, so neither branch in handleSave fired and the flag was silently
  // dropped: the toast said the installer was added, and no QE was ever assigned.
  const wasQE = isEdit && currentQE?.ims_installer_id === installer.id;

  const [name, setName] = useState(installer.name || "");
  const [email, setEmail] = useState(installer.email_address || "");
  const [number, setNumber] = useState(installer.number || "");
  // Read-only: the QE is Team.qe in IMS now, and the design is explicit that a field
  // admin cannot change team composition — "team creation and team retirement stay
  // IMS-only". Shown rather than hidden so the FA can see who it is.
  const isQE = wasQE;

  const isLoading = isSaving;
  const disabled = !name.trim() || !email.trim() || isLoading;

  const handleSave = async () => {
    const result = await saveInstaller({ id: installer.id || undefined, name: name.trim(), email: email.trim(), number: number.trim() });
    if (result.error) return;

    // Saved, but their login wasn't created: IMS's message already says both halves
    // ("Technician saved, but creating their login failed — ..."), and it needs acting on,
    // so it goes out as an error rather than a success that hides it.
    if (result.data?.userport_warning) dispatch(showError(result.data.userport_warning));
    else dispatch(showSuccess(installer.id ? "Installer updated" : "Installer added"));
    onClose();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <PersonOutlineOutlinedIcon sx={{ color: AMBER, fontSize: "1.15rem" }} />
        <Box sx={{ flex: 1, fontSize: "1.05rem", fontWeight: 700 }}>{isEdit ? "Edit installer" : "Add installer"}</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.45)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: BORDER }}>
        <Box sx={{ mb: "14px" }}>
          <FATextField label="Full name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bongani Mthembu" autoFocus />
        </Box>
        <Box sx={{ mb: "14px" }}>
          <FATextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.co.za" />
        </Box>
        <Box sx={{ mb: "16px" }}>
          <FATextField label="Cell number" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="082 555 0000" />
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "10px", p: "11px 12px", backgroundColor: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.2)", borderRadius: "8px", mb: "16px", opacity: isQE ? 1 : 0.55 }}
        >
          <Checkbox
            checked={isQE}
            disabled
            sx={{ p: 0, color: "rgba(168,85,247,0.6)", "&.Mui-checked": { color: "#A855F7" }, "&.Mui-disabled": { color: isQE ? "#A855F7" : "rgba(168,85,247,0.35)" } }}
          />
          <Box>
            <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.85)" }}>Qualified Electrician (QE)</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px" }}>
              Set by your project manager on the team in IMS.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", p: "10px 12px", fontSize: "0.74rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
          New installers onboard here and sync to IMS. IMS remains the system-of-record for installer accounts.
        </Box>

        <FAFieldError error={error} />
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton onClick={handleSave} disabled={disabled}>
          {isLoading ? "Saving…" : "Save installer"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export function RemoveConfirmDialog({ installer, onClose }) {
  const dispatch = useDispatch();
  const [setStatus, { isLoading }] = useSetFieldAdminTeamInstallerStatusMutation();

  const handleConfirm = async () => {
    const result = await setStatus({ installer_id: installer.id, removed: true });
    if (!result.error) {
      dispatch(showSuccess("Installer removed"));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 700 }}>Remove this installer?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
          Removing {installer.name} takes them off the team roster. This is a soft-delete — no hard delete — and their account change proposes to IMS.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton danger onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? "Removing…" : "Remove installer"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export function ChangeVehicleDialog({ vehicles, teamId, teamName, currentVehicleId, onClose }) {
  const dispatch = useDispatch();
  const [saveVehicle, { isLoading }] = useSaveFieldAdminVehicleMutation();
  // Matches the design's own eligibility rule exactly: active, and either
  // unassigned or already assigned to this team (so the current pick shows up too).
  const eligible = vehicles.filter((v) => v.status === "active" && (v.assigned_team_id == null || v.assigned_team_id === teamId));
  const [selectedId, setSelectedId] = useState(currentVehicleId || eligible[0]?.id || "");

  const handleAssign = async () => {
    const vehicle = eligible.find((v) => v.id === selectedId);
    if (!vehicle) { onClose(); return; }
    const result = await saveVehicle({
      id: vehicle.id, make_model: vehicle.make_model, registration: vehicle.registration,
      year: vehicle.year, fuel: vehicle.fuel, odometer_km: vehicle.odometer_km,
      status: vehicle.status, assigned_team_id: teamId,
      last_serviced: vehicle.last_serviced, service_due_in_km: vehicle.service_due_in_km,
    });
    if (!result.error) {
      dispatch(showSuccess(`Vehicle assigned to ${teamLabel(teamName, teamId)}`));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <LocalShippingOutlinedIcon sx={{ color: AMBER, fontSize: "1.15rem" }} />
        <Box sx={{ flex: 1, fontSize: "1.05rem", fontWeight: 700 }}>Assign vehicle</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.45)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {eligible.length === 0 ? (
          <Typography sx={{ fontSize: "0.84rem", color: "rgba(255,255,255,0.55)" }}>
            No active, unassigned vehicles in the fleet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {eligible.map((v) => (
              <Box
                key={v.id}
                onClick={() => setSelectedId(v.id)}
                sx={{
                  display: "flex", alignItems: "center", gap: "10px", p: "10px 12px", borderRadius: "8px", cursor: "pointer",
                  border: selectedId === v.id ? "1px solid rgba(253,185,64,0.5)" : "1px solid rgba(255,255,255,0.08)",
                  backgroundColor: selectedId === v.id ? "rgba(253,185,64,0.08)" : "transparent",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "#fff" }}>{v.make_model} · {v.registration}</Typography>
                  {v.assigned_team_id === teamId ? (
                    <Typography sx={{ fontSize: "0.7rem", color: AMBER, mt: "1px" }}>Current</Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5, mt: "14px" }}>
          Only active, unassigned vehicles from the fleet register can be assigned. One vehicle per team — assigning here releases the team's current vehicle back to the pool.{" "}
          <Box component={Link} to="/field-admin/more/fleet/" sx={{ color: AMBER, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
            Open fleet register →
          </Box>
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton onClick={handleAssign} disabled={eligible.length === 0 || isLoading}>
          {isLoading ? "Assigning…" : "Assign vehicle"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
